import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const [selectedCert, setSelectedCert] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      title: '高效過濾',
      metric: 'PM2.5過濾效能',
      value: '>95%',
      description: '採用精密編織技術,有效阻隔PM2.5、灰塵、蚊蟲、花粉等微小顆粒',
      bgColor: 'bg-emerald-50',
      image: 'https://public.readdy.ai/ai/img_res/3d4675f7caaf6d78777eba15230572a9.jpg'
    },
    {
      title: '透氣通風',
      metric: '通風率',
      value: '55%-60%',
      description: '獨特的網孔設計,在高效過濾的同時保持良好的空氣流通性',
      bgColor: 'bg-emerald-50',
      image: 'https://readdy.ai/api/search-image?query=modern%20open%20window%20with%20white%20curtains%20gently%20flowing%20in%20fresh%20breeze%20natural%20wind%20blowing%20into%20bright%20sunny%20room%20comfortable%20ventilation%20effect%20peaceful%20home%20interior%20showing%20excellent%20air%20circulation%20and%20natural%20airflow%20through%20window&width=400&height=300&seq=windowbreeze015&orientation=landscape'
    },
    {
      title: '耐用材質',
      metric: '使用壽命',
      value: '8年+',
      description: '採用高強度聚酯纖維,抗UV、防腐蝕,經久耐用不變形',
      bgColor: 'bg-gray-50',
      image: 'https://readdy.ai/api/search-image?query=futuristic%20high%20tech%20durable%20stone%20material%20with%20metallic%20coating%20showing%20advanced%20technology%20surface%20treatment%20strong%20solid%20rock%20texture%20with%20modern%20scientific%20coating%20representing%20long%20lasting%20durability%20and%20strength%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=techstone015&orientation=landscape'
    },
    {
      title: '易於清潔',
      metric: '清潔週期',
      value: '3個月',
      description: '表面防污處理,只需清水沖洗即可恢復如新,維護簡單方便',
      bgColor: 'bg-emerald-50',
      image: 'https://readdy.ai/api/search-image?query=person%20hand%20holding%20plant%20mister%20spray%20bottle%20spraying%20water%20mist%20onto%20white%20window%20screen%20mesh%20visible%20water%20droplets%20and%20spray%20coming%20out%20from%20nozzle%20easy%20cleaning%20maintenance%20routine%20bright%20home%20environment%20demonstrating%20simple%20care%20process&width=400&height=300&seq=sprayclean015&orientation=landscape'
    }
  ];

  const products = [
    {
      type: '高防護',
      name: 'NH - 防霾紗網',
      image: 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20photography%20of%20NH%20anti%20haze%20screen%20mesh%20ultra%20fine%20white%20mesh%20texture%20showing%20microscopic%20weave%20pattern%20professional%20product%20photography%20against%20pure%20white%20background%20studio%20lighting%20high%20detail&width=300&height=300&seq=nh001&orientation=squarish',
      filterRate: '95%',
      ventilationRate: '55%',
      features: ['高效防霾', '隔絕強', '適合都市'],
      applications: ['住宅公寓', '辦公大樓', '醫療機構'],
      description: '有效阻隔PM2.5及細小顆粒，適合空氣品質較差的都市環境',
      detailedSpecs: {
        meshSize: '0.15mm',
        material: '高密度聚酯纖維',
        thickness: '0.8mm',
        weight: '180g/m²',
        uvResistance: 'UPF 50+',
        warranty: '3年'
      },
      category: '住宅'
    },
    {
      type: '防煙防塵',
      name: 'DSP - 防煙網',
      image: 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20photography%20of%20DSP%20smoke%20prevention%20screen%20white%20fine%20mesh%20texture%20showing%20detailed%20weave%20pattern%20professional%20product%20photography%20against%20pure%20white%20background%20studio%20lighting%20high%20detail&width=300&height=300&seq=dsp001&orientation=squarish',
      filterRate: '92%',
      ventilationRate: '60%',
      features: ['減緩異味', '高效過濾', '高污染區'],
      applications: ['工業廠房', '商業空間', '餐飲場所'],
      description: '減緩異味，有效阻隔二手煙，油煙味，工業煙塵等污染物五至七成',
      detailedSpecs: {
        meshSize: '0.18mm',
        material: '防火聚酯纖維',
        thickness: '0.9mm',
        weight: '200g/m²',
        uvResistance: 'UPF 45+',
        warranty: '3年'
      },
      category: '商業'
    },
    {
      type: '平衡型',
      name: 'DP - 防霾網',
      image: 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20photography%20of%20DP%20anti%20haze%20screen%20white%20medium%20mesh%20texture%20showing%20clear%20weave%20pattern%20professional%20product%20photography%20against%20pure%20white%20background%20studio%20lighting%20high%20detail&width=300&height=300&seq=dp001&orientation=squarish',
      filterRate: '88%',
      ventilationRate: '70%',
      features: ['平衡型設計', '通風佳', '經濟實惠'],
      applications: ['住宅公寓', '教育機構', '一般辦公'],
      description: '過濾與通風的完美平衡，提供良好的空氣品質同時保持室內通風，性價比高',
      detailedSpecs: {
        meshSize: '0.20mm',
        material: '標準聚酯纖維',
        thickness: '0.7mm',
        weight: '160g/m²',
        uvResistance: 'UPF 40+',
        warranty: '3年'
      },
      category: '住宅'
    },
    {
      type: '通風型',
      name: 'HB - 高透氣防塵網',
      image: 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20macro%20photography%20of%20HB%20high%20breathability%20dust%20prevention%20screen%20white%20coarse%20mesh%20texture%20showing%20large%20weave%20pattern%20professional%20product%20photography%20against%20pure%20white%20background%20studio%20lighting%20high%20detail&width=300&height=300&seq=hb001&orientation=squarish',
      filterRate: '50%',
      ventilationRate: '85%',
      features: ['超高通風', '防大顆粒', '透光性佳'],
      applications: ['陽台空間', '通風需求高', '半開放空間'],
      description: '超高通風率設計，有效阻隔灰塵、蚊蟲等大顆粒，適合需要大量通風的空間',
      detailedSpecs: {
        meshSize: '0.25mm',
        material: '輕量聚酯纖維',
        thickness: '0.6mm',
        weight: '140g/m²',
        uvResistance: 'UPF 35+',
        warranty: '3年'
      },
      category: '住宅'
    }
  ];

  const certifications = [
    {
      title: 'PM2.5 過濾效能',
      subtitle: '國家標準檢測認證',
      color: 'emerald',
      icon: 'ri-shield-check-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20laboratory%20testing%20report%20document%20showing%20PM2.5%20filtration%20efficiency%20test%20results%20official%20certification%20paper%20with%20graphs%20charts%20data%20tables%20clean%20white%20background%20professional%20photography&width=600&height=500&seq=cert001&orientation=landscape',
      metrics: [
        { label: 'PM2.5 過濾率', value: '>95%' },
        { label: 'PM10 過濾率', value: '>98%' },
        { label: '檢測標準', value: 'CNS 15980' }
      ]
    },
    {
      title: '通風性能測試',
      subtitle: 'SGS 國際認證',
      color: 'blue',
      icon: 'ri-wind-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20laboratory%20airflow%20ventilation%20testing%20equipment%20measuring%20screen%20mesh%20breathability%20official%20SGS%20certification%20document%20with%20technical%20data%20clean%20white%20background%20professional%20photography&width=600&height=500&seq=cert002&orientation=landscape',
      metrics: [
        { label: '通風率', value: '55-60%' },
        { label: '風阻係數', value: '0.35' },
        { label: '檢測標準', value: 'ISO 9237' }
      ]
    },
    {
      title: '耐候性測試',
      subtitle: '工研院檢測認證',
      color: 'amber',
      icon: 'ri-sun-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20laboratory%20UV%20resistance%20weathering%20test%20equipment%20testing%20screen%20mesh%20durability%20official%20certification%20document%20with%20test%20results%20clean%20white%20background%20professional%20photography&width=600&height=500&seq=cert003&orientation=landscape',
      metrics: [
        { label: 'UV 抗性', value: 'UPF 50+' },
        { label: '耐候年限', value: '8年+' },
        { label: '檢測標準', value: 'CNS 15001' }
      ]
    },
    {
      title: '材質安全認證',
      subtitle: '環保署認證',
      color: 'green',
      icon: 'ri-leaf-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20laboratory%20material%20safety%20testing%20report%20showing%20non-toxic%20certification%20environmental%20protection%20official%20document%20with%20safety%20data%20clean%20white%20background%20professional%20photography&width=600&height=500&seq=cert004&orientation=landscape',
      metrics: [
        { label: '無毒認證', value: '通過' },
        { label: '環保等級', value: 'A+' },
        { label: '檢測標準', value: 'CNS 14705' }
      ]
    }
  ];

  const faqs = [
    {
      question: '防霾紗網真的能有效阻隔 PM2.5 嗎？',
      answer: '是的！我們的防霾紗網經過國家標準檢測認證，PM2.5 過濾效能達 95% 以上。採用精密編織技術，網孔尺寸經過精密計算，能有效阻隔細小顆粒，同時保持良好通風。'
    },
    {
      question: '安裝防霾紗網後會不會影響通風？',
      answer: '不會的！我們的產品設計兼顧過濾與通風，通風率可達 55-60%。獨特的網孔設計確保空氣流通順暢，讓您在享受清新空氣的同時，也能保持室內良好的通風環境。'
    },
    {
      question: '防霾紗網需要多久清潔一次？',
      answer: '建議每 3 個月清潔一次。我們的紗網表面經過防污處理，清潔非常簡單，只需用清水沖洗或濕布擦拭即可。定期清潔可以維持最佳的過濾效能。'
    },
    {
      question: '防霾紗網的使用壽命有多長？',
      answer: '我們的防霾紗網採用高強度聚酯纖維材質，具有優異的抗UV和防腐蝕性能，正常使用下壽命可達 8 年以上。我們提供 3 年免費保固，讓您使用更安心。'
    },
    {
      question: '可以只更換紗網，不換整個窗框嗎？',
      answer: '可以的！我們提供單獨更換紗網的服務。技師會到府評估您現有的窗框狀況，如果窗框狀況良好，只需更換紗網即可，既經濟又環保。'
    },
    {
      question: '安裝需要多久時間？',
      answer: '一般住宅的安裝時間約 2-4 小時，實際時間依窗戶數量和尺寸而定。我們的專業團隊會在預約時間準時到達，快速完成安裝，不會過度打擾您的日常生活。'
    },
    {
      question: '你們的服務範圍涵蓋哪些地區？',
      answer: '我們主要服務大台北地區，包括台北市、新北市。其他地區如桃園、新竹等也可服務，歡迎透過 LINE 諮詢，我們會為您安排最適合的服務時間。'
    },
    {
      question: '價格大概是多少？',
      answer: '價格會依據窗戶尺寸、選擇的紗網類型、安裝難度等因素而有所不同。我們提供免費到府丈量和報價服務，會給您最透明、最合理的價格，絕無隱藏費用。'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img src="https://static.readdy.ai/image/8911452f32edcda69de2ede9d29b7219/72b9940bda2dbcae80f75e87e5b73e8a.jpeg" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-gray-900">達斯特防霾紗窗</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#products" className="text-gray-700 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">產品介紹</a>
            <a href="#technology" className="text-gray-700 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">防霾技術</a>
            <a href="#cases" className="text-gray-700 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">應用案例</a>
            <a href="#faq" className="text-gray-700 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">常見問題</a>
            <a href="#contact" className="text-gray-700 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">聯絡我們</a>
          </div>
          <a href="http://lin.ee/uoynqZC" target="_blank" rel="noopener noreferrer" className="bg-emerald-600 text-white px-6 py-2.5 rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
            立即諮詢
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-1.5 border border-emerald-600 rounded-full text-emerald-600 text-sm font-medium whitespace-nowrap">
                ANTI-HAZE TECHNOLOGY
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                在家好好呼吸<br />
                <span className="text-gray-900">專業防霾紗網</span><br />
                <span className="text-gray-600 text-4xl">防霾紗窗、鋁門窗、陽台包覆工程</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                防塵、防雨、防蟲，美觀又好清潔，<br />
                我們懂你在意的細節，最期待遇見講究生活的你。
              </p>
              <div className="flex gap-4 pt-4">
                <a href="#products" className="bg-emerald-600 text-white px-8 py-3.5 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  了解產品
                  <i className="ri-arrow-right-line"></i>
                </a>
                <button className="bg-white text-emerald-600 px-8 py-3.5 rounded-lg border-2 border-emerald-600 hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer">
                  認證報告
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-3xl overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8911452f32edcda69de2ede9d29b7219/f1981713687af42c47e567219c827ee6.jpeg" 
                  alt="防霾紗網安裝效果" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="technology" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-3">
                為什麼選擇我們的<span className="text-emerald-600">防霾紗網</span>
              </h2>
              <p className="text-gray-600">四大核心技術優勢</p>
            </div>
            <button className="hidden md:block px-6 py-2 bg-emerald-600 text-white rounded-full text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer">
              查看完整規格
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className={`${feature.bgColor} rounded-2xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer`}>
                <div className="w-full h-48">
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-bold text-emerald-600">{feature.value}</span>
                    <span className="text-sm text-gray-600">{feature.metric}</span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-3">
                產品<span className="text-emerald-600">系列</span>
              </h2>
              <p className="text-gray-600">適配不同場景需求</p>
            </div>
            <a 
              href="http://lin.ee/uoynqZC" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:block px-6 py-2 bg-emerald-600 text-white rounded-full text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部產品
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {products.map((product, index) => (
              <div 
                key={index} 
                className="relative bg-gray-50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
                onMouseEnter={() => setHoveredProduct(index)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                {/* Product Image */}
                <div className="w-full h-64 relative overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Hover Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-emerald-900/95 via-emerald-800/80 to-transparent transition-opacity duration-300 ${hoveredProduct === index ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <p className="text-sm font-semibold mb-3 text-emerald-100">核心規格</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-emerald-200">過濾效能</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-emerald-900/50 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-300 rounded-full transition-all duration-1000"
                                style={{ width: hoveredProduct === index ? product.filterRate : '0%' }}
                              ></div>
                            </div>
                            <span className="text-sm font-bold text-white">{product.filterRate}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-emerald-200">通風率</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-emerald-900/50 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-300 rounded-full transition-all duration-1000 delay-100"
                                style={{ width: hoveredProduct === index ? product.ventilationRate : '0%' }}
                              ></div>
                            </div>
                            <span className="text-sm font-bold text-white">{product.ventilationRate}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <p className="text-base text-gray-500 mb-2">{product.type}</p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">{product.name}</h3>
                  
                  {/* Features Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Description - Always visible */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {product.description}
                  </p>

                  {/* Quick Stats */}
                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{product.filterRate}</p>
                      <p className="text-xs text-gray-500">防塵效果（相對值）</p>
                    </div>
                    <div className="w-px h-10 bg-gray-200"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{product.ventilationRate}</p>
                      <p className="text-xs text-gray-500">透氣效果（相對值）</p>
                    </div>
                  </div>
                </div>

                {/* Corner Badge */}
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 text-white rounded-full shadow-lg group-hover:scale-110 transition-transform">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Product Comparison Hint */}
          <div className="mt-12 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl flex-shrink-0">
                  <i className="ri-question-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">不確定選擇哪款產品？</h3>
                  <p className="text-gray-600 text-sm">讓我們的專業顧問為您推薦最適合的防霾紗網方案</p>
                </div>
              </div>
              <a 
                href="http://lin.ee/uoynqZC" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-emerald-600 text-white px-8 py-3.5 rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer font-semibold"
              >
                <i className="ri-customer-service-2-line text-xl"></i>
                免費諮詢
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-emerald-600 text-white rounded-full text-sm font-semibold mb-4">
              CERTIFICATIONS & TEST REPORTS
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              專業<span className="text-emerald-600">認證報告</span>
            </h2>
            <p className="text-gray-600">權威機構檢測認證，品質值得信賴</p>
          </div>

          {/* Main Certification Display */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-12">
            <div className="grid md:grid-cols-2">
              <div className="p-12 flex flex-col justify-center">
                <div className={`inline-flex items-center gap-2 px-4 py-2 bg-${certifications[selectedCert].color}-100 text-${certifications[selectedCert].color}-700 rounded-full text-sm font-semibold mb-6 w-fit`}>
                  <i className={`${certifications[selectedCert].icon} text-lg`}></i>
                  認證報告
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  {certifications[selectedCert].title}
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  {certifications[selectedCert].subtitle}
                </p>
                
                <div className="space-y-4 mb-8">
                  {certifications[selectedCert].metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-700 font-medium">{metric.label}</span>
                      <span className={`text-2xl font-bold text-${certifications[selectedCert].color}-600`}>
                        {metric.value}
                      </span>
                    </div>
                  ))}
                </div>

                <button className="bg-emerald-600 text-white px-8 py-3.5 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-download-line"></i>
                  下載完整報告
                </button>
              </div>
              
              <div className="relative bg-gray-100 min-h-[500px]">
                <img 
                  src={certifications[selectedCert].image}
                  alt={certifications[selectedCert].title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                  <span className="text-sm font-bold text-gray-900">官方認證</span>
                </div>
              </div>
            </div>
          </div>

          {/* Certification Tabs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {certifications.map((cert, index) => (
              <button
                key={index}
                onClick={() => setSelectedCert(index)}
                className={`p-6 rounded-2xl border-2 transition-all cursor-pointer ${
                  selectedCert === index
                    ? 'border-emerald-600 bg-emerald-50 shadow-lg'
                    : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${
                  selectedCert === index ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <i className={`${cert.icon} text-2xl`}></i>
                </div>
                <h4 className={`font-bold text-sm mb-1 ${
                  selectedCert === index ? 'text-emerald-600' : 'text-gray-900'
                }`}>
                  {cert.title}
                </h4>
                <p className="text-xs text-gray-600">{cert.subtitle}</p>
              </button>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="mt-16 bg-white rounded-3xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">權威機構認可</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-3">
                  <i className="ri-government-line text-4xl text-gray-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-900">經濟部標準檢驗局</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-3">
                  <i className="ri-shield-check-line text-4xl text-gray-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-900">SGS 國際認證</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-3">
                  <i className="ri-building-line text-4xl text-gray-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-900">國立陽明交通大學</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-3">
                  <i className="ri-test-tube-line text-4xl text-gray-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-900">工研院檢測中心</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-3">
                  <i className="ri-leaf-line text-4xl text-gray-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-900">環保署認證</p>
              </div>
            </div>
          </div>

          {/* Key Highlights */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-4">
                <i className="ri-medal-line text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold mb-2">100% 合格</h4>
              <p className="text-emerald-100">所有產品通過國家標準檢測，品質保證無虞</p>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-4">
                <i className="ri-microscope-line text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold mb-2">嚴格檢測</h4>
              <p className="text-blue-100">每批產品出廠前經過多重品質檢測程序</p>
            </div>
            <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-4">
                <i className="ri-file-shield-line text-4xl"></i>
              </div>
              <h4 className="text-2xl font-bold mb-2">完整文件</h4>
              <p className="text-amber-100">提供完整檢測報告與認證文件供客戶查閱</p>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section - Timeline Process */}
      <section id="cases" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1.5 bg-emerald-600 text-white rounded-full text-sm font-semibold mb-4">
              INSTALLATION PROCESS & CASES
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              從諮詢到完工<span className="text-emerald-600">全程服務</span>
            </h2>
            <p className="text-gray-600">專業流程，讓您安心無憂</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Timeline Process */}
            <div className="relative">
              <div className="absolute left-8 top-0 h-[calc(100%-4rem)] w-1 bg-gradient-to-b from-emerald-600 via-emerald-500 to-emerald-400"></div>
              
              <div className="space-y-8">
                {/* Step 1 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-customer-service-2-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 01</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">LINE 線上諮詢</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      專業顧問即時回覆，了解您的需求，提供初步建議與產品介紹
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">免費諮詢</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">即時回覆</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">專業建議</span>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-calendar-check-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 02</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">預約到府丈量</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      安排專業技師到府精準測量，評估現場環境，確保尺寸完全符合
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">免費丈量</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">精準測量</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">現場評估</span>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-file-list-3-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 03</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">報價單確認</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      提供報價單，包含材質、價格、保固等完整資訊，透明公開
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">透明報價</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">詳細說明</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">無隱藏費用</span>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-hammer-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 04</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">客製化製作</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      根據測量尺寸進行客製化製作，嚴格品質把關，確保每個細節完美
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">客製化</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">品質把關</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">精密製作</span>
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-tools-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 05</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">專業安裝施工</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      經驗豐富的安裝團隊到府施工，標準化作業流程，確保安裝品質
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">專業團隊</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">標準作業</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">快速完工</span>
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div className="relative pl-20">
                  <div className="absolute left-0 top-0 w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-2xl shadow-lg z-10">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-600">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">STEP 06</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">驗收與售後服務</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      現場驗收確認，提供保固卡與使用說明，長期售後服務保障
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">完工驗收</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">保固服務</span>
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">品質保證</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Case Studies */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">服務成果統計</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">200+</div>
                    <div className="text-emerald-100 text-sm">滿意客戶</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">100%</div>
                    <div className="text-emerald-100 text-sm">品質保證</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">3年</div>
                    <div className="text-emerald-100 text-sm">免費保固</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">專業</div>
                    <div className="text-emerald-100 text-sm">服務團隊</div>
                  </div>
                </div>
              </div>

              {/* Case Photos */}
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">實際案例</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="relative rounded-xl overflow-hidden group cursor-pointer">
                    <div className="w-full h-64">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20residential%20apartment%20living%20room%20with%20anti%20haze%20screen%20installed%20on%20large%20windows%20clean%20bright%20interior%20natural%20lighting%20comfortable%20home%20environment%20professional%20interior%20photography&width=400&height=400&seq=case001&orientation=squarish"
                        alt="住宅案例"
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="font-bold text-lg">住宅公寓</p>
                        <p className="text-sm text-gray-200">台北市信義區</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden group cursor-pointer">
                    <div className="w-full h-64">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20office%20building%20workspace%20with%20anti%20haze%20screen%20installed%20on%20windows%20professional%20business%20environment%20clean%20air%20quality%20bright%20natural%20lighting%20commercial%20interior%20photography&width=400&height=400&seq=case002&orientation=squarish"
                        alt="辦公案例"
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="font-bold text-lg">辦公大樓</p>
                        <p className="text-sm text-gray-200">新北市板橋區</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden group cursor-pointer">
                    <div className="w-full h-64">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20school%20classroom%20with%20anti%20haze%20screen%20installed%20on%20windows%20bright%20educational%20environment%20students%20learning%20clean%20air%20quality%20natural%20lighting%20educational%20interior%20photography&width=400&height=400&seq=case003&orientation=squarish"
                        alt="學校案例"
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="font-bold text-lg">教育機構</p>
                        <p className="text-sm text-gray-200">桃園市中壢區</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden group cursor-pointer">
                    <div className="w-full h-64">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20hospital%20medical%20facility%20with%20anti%20haze%20screen%20installed%20on%20windows%20clean%20hygienic%20healthcare%20environment%20professional%20medical%20interior%20bright%20natural%20lighting%20healthcare%20photography&width=400&height=400&seq=case004&orientation=squarish"
                        alt="醫療案例"
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="font-bold text-lg">醫療機構</p>
                        <p className="text-sm text-gray-200">台中市西屯區</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              常見<span className="text-emerald-600">問題</span>
            </h2>
            <p className="text-gray-600">解答您的疑惑</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-emerald-50 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-2xl text-emerald-600 flex-shrink-0`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              立即預約<span className="text-emerald-600">免費到府丈量</span>
            </h2>
            <p className="text-gray-600">專業團隊為您服務，從諮詢到安裝一站式解決</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-8 md:p-12">
            {/* Service Highlights */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-full mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">專業諮詢</h3>
                <p className="text-sm text-gray-600">LINE 即時回覆<br />解答所有疑問</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-full mx-auto mb-4">
                  <i className="ri-ruler-line text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">免費量尺</h3>
                <p className="text-sm text-gray-600">到府精準測量<br />提供最佳方案</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-600 text-white rounded-full mx-auto mb-4">
                  <i className="ri-tools-line text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">專業安裝</h3>
                <p className="text-sm text-gray-600">經驗豐富的團隊<br />品質有保證</p>
              </div>
            </div>

            {/* Service Process */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">服務流程</h3>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">1</div>
                  <span className="font-semibold text-gray-900">LINE 諮詢</span>
                </div>
                <i className="ri-arrow-right-line text-2xl text-emerald-600 hidden md:block"></i>
                <i className="ri-arrow-down-line text-2xl text-emerald-600 md:hidden"></i>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">2</div>
                  <span className="font-semibold text-gray-900">預約丈量</span>
                </div>
                <i className="ri-arrow-right-line text-2xl text-emerald-600 hidden md:block"></i>
                <i className="ri-arrow-down-line text-2xl text-emerald-600 md:hidden"></i>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">3</div>
                  <span className="font-semibold text-gray-900">報價確認</span>
                </div>
                <i className="ri-arrow-right-line text-2xl text-emerald-600 hidden md:block"></i>
                <i className="ri-arrow-down-line text-2xl text-emerald-600 md:hidden"></i>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">4</div>
                  <span className="font-semibold text-gray-900">專業安裝</span>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <a 
                href="http://lin.ee/uoynqZC" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-emerald-600 text-white px-12 py-5 rounded-full text-lg font-bold hover:bg-emerald-700 transition-all hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-line-fill text-2xl"></i>
                加入 LINE 立即諮詢
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <p className="text-sm text-gray-600 mt-6">點擊按鈕即可開啟 LINE 對話，我們將立即為您服務</p>
              
              {/* Contact Info */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-phone-line text-emerald-600"></i>
                  <span className="text-sm">服務專線：0955-205-422</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-time-line text-emerald-600"></i>
                  <span className="text-sm">服務時間：週一至週五 10:00-19:30</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-3xl font-bold mb-4">達斯特防霾紗窗</h3>
              <p className="text-emerald-100 leading-relaxed mb-6">
                專業防霾紗窗、鋁門窗、陽台包覆工程，致力於為客戶提供高品質的空氣防護解決方案。
              </p>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/share/1DZv3Sbkkv/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-emerald-500 hover:border-white hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="https://www.instagram.com/duster_tw?igsh=Z3BlOWl4ZTk2eDAz" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-emerald-500 hover:border-white hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="http://lin.ee/uoynqzC" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-emerald-500 hover:border-white hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-line-fill"></i>
                </a>
                <a href="https://youtube.com/@dustertw?si=lL7QG9l56X2N4Htv" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-emerald-500 hover:border-white hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 tracking-wider">聯絡地址</h4>
              <p className="text-emerald-100 leading-relaxed">
                新北市汐止區環河街189巷5號
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 tracking-wider">服務熱線</h4>
              <p className="text-emerald-100 mb-4">0955-205-422</p>
              <h4 className="text-sm font-semibold mb-4 tracking-wider">商務郵箱</h4>
              <p className="text-emerald-100">dustertw@gmail.com</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 tracking-wider">服務保證</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-emerald-600 rounded-lg p-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                    <i className="ri-shield-check-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">免費保固</p>
                    <p className="text-emerald-100 text-xs">平面式三年 / 卷軸式一年</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-emerald-600 rounded-lg p-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                    <i className="ri-percent-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">六折換新</p>
                    <p className="text-emerald-100 text-xs">憑保固卡六折換新保障</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-emerald-600 rounded-lg p-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                    <i className="ri-team-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">直營團隊</p>
                    <p className="text-emerald-100 text-xs">標準化專業安裝服務</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-emerald-600 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-emerald-100">© 2024 達斯特防霾紗窗. All rights reserved.</p>
            <div className="flex gap-6 text-sm text-emerald-100">
              <a href="#" className="hover:text-white transition-colors cursor-pointer">隱私政策</a>
              <a href="#" className="hover:text-white transition-colors cursor-pointer">使用條款</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}